import { UserRole, userRoleMapping } from "constants/userRole";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useGetAllAccountsQuery } from "store/queries/account";
import {
  useGetMyTrainingQuery,
  useResetTrainingMutation,
  useWaiveTrainingMutation,
} from "store/queries/training";
import { userSelector } from "store/slices/user";
import { bannerAction, trainingProgressLabel } from "training/quizHelpers";
import { ITrainingRecord } from "types/Training";
import { IUserResponse } from "types/User";

import {
  CertifiedChip,
  formatCertifiedDate,
  isCertifiedDate,
} from "./CertifiedChip";

type ITrainingState = Pick<
  ITrainingRecord,
  "clearCertifiedAt" | "trainingProgress"
> & {
  /** The API's curriculum version, once known (a waive / reset response carries it). */
  curriculumVersion?: number;
};

const fromUser = (user: IUserResponse): ITrainingState => ({
  clearCertifiedAt: user.clearCertifiedAt ?? null,
  trainingProgress: user.trainingProgress ?? null,
});

interface IUserTrainingStatusProps {
  user: IUserResponse;
}

/**
 * CLE-3015 Clear Certified — the training row on the SuperAdmin user detail page.
 *
 * For a HospitalStaff user the status line says what the user will experience (John, dev 2026-10-29: "Not
 * started" alone read as a dead end): not started → they will see the banner on the Estimates tab; in progress →
 * "Module N of 6" or "final knowledge check next" (the banner's own `bannerAction`); certified → the chip and the
 * date. "Mark certified (trained in person)" (waive) while not certified, "Reset training" once certified or once
 * any progress exists. Each asks for confirmation first.
 *
 * Any other role gets a note that the training is for hospital staff, plus their status if they have training data
 * (e.g. they were HospitalStaff before), and never the buttons.
 *
 * Hidden entirely unless one of the user's accounts has `features.clearCertifiedTraining` on or
 * the user already has training data — so nothing about the training shows before rollout.
 *
 * The state is kept locally from each mutation's response rather than by refetching the user:
 * a refetch would re-seed (and discard edits in) the user form around it.
 *
 * The curriculum version (progress saved under an older one reads as not started) is not on the user record: it
 * comes from the viewer's own GET /user/me/training, which carries the API's version for anyone signed in, or
 * from a waive / reset response. Until it is known an in-progress user reads plain "In progress".
 */
export function UserTrainingStatus({ user }: IUserTrainingStatusProps) {
  const { current: toast } = useContext(ToastContext) ?? { current: null };
  const { data: accounts } = useGetAllAccountsQuery();
  const [waiveTraining, { isLoading: waiving }] = useWaiveTrainingMutation();
  const [resetTraining, { isLoading: resetting }] = useResetTrainingMutation();
  const [state, setState] = useState<ITrainingState>(() => fromUser(user));
  const { currentAccount } = useSelector(userSelector);
  const { data: myTraining } = useGetMyTrainingQuery(currentAccount, {
    skip: !state.trainingProgress,
  });

  useEffect(() => {
    setState(fromUser(user));
  }, [user.id, user.clearCertifiedAt, user.trainingProgress]);

  const flagOnForUser = (user.accountIds ?? []).some(
    (accountId) =>
      accounts?.data?.find((account) => account.id === accountId)?.features
        ?.clearCertifiedTraining === true
  );
  const hasTrainingData = Boolean(
    state.clearCertifiedAt || state.trainingProgress
  );

  if (!flagOnForUser && !hasTrainingData) {
    return null;
  }

  const isHospitalStaff = user.scope === UserRole.HOSPITAL_STAFF;
  const certified = isCertifiedDate(state.clearCertifiedAt);
  const showWaive = isHospitalStaff && !certified;
  const showReset = isHospitalStaff && hasTrainingData;
  const name = user.name?.trim() || "";
  const subject = name || "They";
  const roleLabel =
    userRoleMapping[user.scope as UserRole] ?? user.scope ?? "These";

  const curriculumVersion =
    state.curriculumVersion ?? myTraining?.data?.curriculumVersion;
  // The SuperAdmin's own browser session says nothing about this user's quizzes, so no session passes.
  const progressLabel =
    curriculumVersion === undefined
      ? null
      : trainingProgressLabel(
          bannerAction(
            {
              curriculumVersion,
              trainingProgress: state.trainingProgress,
            },
            []
          )
        );

  let statusText: string | null = null;
  if (certified) {
    statusText = `Certified ${formatCertifiedDate(
      state.clearCertifiedAt as string
    )}.`;
  } else if (state.trainingProgress && curriculumVersion === undefined) {
    statusText = "In progress.";
  } else if (progressLabel) {
    statusText = `In progress, ${progressLabel}.`;
  } else if (isHospitalStaff) {
    statusText = `Not started. ${subject} will see a "Get Clear Certified" banner on the Estimates tab when they log in.`;
  } else if (hasTrainingData) {
    statusText = "Not started.";
  }

  const run = async (
    action: () => Promise<{ data: ITrainingRecord }>,
    success: string
  ) => {
    try {
      const { data } = await action();
      setState({
        clearCertifiedAt: data.clearCertifiedAt,
        trainingProgress: data.trainingProgress,
        curriculumVersion: data.curriculumVersion,
      });
      toast?.show({ severity: "success", summary: success });
    } catch {
      toast?.show({
        severity: "error",
        summary: "Something went wrong",
        detail: "The training status was not changed.",
      });
    }
  };

  const onWaive = () =>
    confirmDialog({
      header: name ? `Mark ${name} certified?` : "Mark this user certified?",
      message: `This skips the training and marks ${
        name || "this user"
      } Clear Certified. The training banner stops showing for them. Nothing is reset.`,
      acceptLabel: "Mark certified",
      rejectLabel: "Cancel",
      accept: () =>
        run(() => waiveTraining(user.id).unwrap(), "Marked Clear Certified"),
    });

  const onReset = () =>
    confirmDialog({
      header: "Reset training?",
      message: `This clears ${user.name}'s training progress and certificate. The training banner will show again.`,
      acceptLabel: "Reset training",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () =>
        run(() => resetTraining(user.id).unwrap(), "Training reset"),
    });

  return (
    <div className="field col-12 mt-4" data-testid="user-training-status">
      <h4 className="m-0 mb-2 flex align-items-center gap-2">
        Clear Certified training
        <CertifiedChip certifiedAt={state.clearCertifiedAt} />
      </h4>
      {statusText && (
        <p
          className="m-0 mb-2 text-sm text-gray-600"
          data-testid="user-training-status-text"
        >
          {statusText}
        </p>
      )}
      {!isHospitalStaff && (
        <p
          className="m-0 mb-2 text-sm text-gray-500 font-italic"
          data-testid="user-training-role-note"
        >
          Training is for hospital staff. {roleLabel} users don&apos;t take it.
        </p>
      )}
      <div className="flex gap-2">
        {showWaive && (
          <Button
            type="button"
            className="p-button-sm p-button-outlined"
            label="Mark certified (trained in person)"
            icon="pi pi-check"
            loading={waiving}
            disabled={waiving || resetting}
            onClick={onWaive}
          />
        )}
        {showReset && (
          <Button
            type="button"
            className="p-button-sm p-button-outlined p-button-danger"
            label="Reset training"
            icon="pi pi-refresh"
            loading={resetting}
            disabled={waiving || resetting}
            onClick={onReset}
          />
        )}
      </div>
      {showWaive && (
        <p className="m-0 mt-2 text-xs text-gray-600">
          Skips the tour and marks them Clear Certified. Use this for staff
          trained live.
        </p>
      )}
    </div>
  );
}
